import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import type { RegistryDocument } from '../../data/types';
import DocumentViewerDialog from '../../documents/DocumentViewerDialog';
import CompletedInvestments from './CompletedInvestments';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { date, money, unitPrice, units as formatUnits } from '../../lib/format';
import { sumBy, useInvestorScope } from '../../store/selectors';
import { AboutThisPage } from '../../ui/primitives';
import { Card, EntityFilter, InvestorTitle, shortEntityName } from '../common';

const COLORS = ['#143c34', '#1E7F6C', '#adc9a8', '#4a6854', '#c2d4bd', '#0e2a24'];

export default function PortfolioPage() {
  const scope = useInvestorScope();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [viewing, setViewing] = useState<RegistryDocument | null>(null);
  const entity = params.get('entity') ?? 'ALL';
  const holdings = useMemo(
    () => scope.holdings.filter((holding) => entity === 'ALL' || holding.holderId === entity),
    [scope.holdings, entity],
  );
  const total = sumBy(holdings, (holding) => holding.value);
  const byFund = useMemo(() => {
    const map = new Map<string, number>();
    for (const holding of holdings)
      map.set(holding.fundId, (map.get(holding.fundId) ?? 0) + holding.value);
    return Array.from(map.entries()).map(([fundId, value]) => ({
      name: scope.funds.find((fund) => fund.id === fundId)?.code ?? fundId,
      value: Math.round(value),
    }));
  }, [holdings, scope.funds]);

  return (
    <>
      <InvestorTitle
        title="Portfolio"
        subtitle={`${money(total)} across ${holdings.length} holdings`}
      />
      <EntityFilter
        holders={scope.holders}
        value={entity}
        onChange={(next) => setParams(next === 'ALL' ? {} : { entity: next })}
      />
      <AboutThisPage>
        <p>
          Each holding shows your units on the register, today&apos;s unit price and value. For
          commitment funds you also see how much of your commitment has been called. Select a
          holding for its full history.
        </p>
      </AboutThisPage>
      <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' } }}>
        <Stack spacing={1.25}>
          {holdings.map((holding) => {
            const fund = scope.funds.find((item) => item.id === holding.fundId);
            const unitClass = scope.classes.find((item) => item.id === holding.classId);
            const holder = scope.holders.find((item) => item.id === holding.holderId);
            const income = sumBy(
              scope.ledger.filter(
                (entry) =>
                  entry.holderId === holding.holderId &&
                  entry.classId === holding.classId &&
                  entry.type === 'DISTRIBUTION' &&
                  entry.effectiveDate >= '2026-07-01',
              ),
              (entry) => entry.amount,
            );
            return (
              <Card
                key={`${holding.holderId}-${holding.classId}`}
                onClick={() =>
                  navigate(`/investor/holdings/${holding.holderId}/${holding.classId}`)
                }
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  spacing={1.5}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      {fund?.strategy} · {unitClass?.name}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                      {fund?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {holder ? shortEntityName(holder) : ''} · since {date(holding.firstDate)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: { sm: 'right' } }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.2rem' }}>
                      {money(holding.value)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatUnits(holding.units)} units @ {unitPrice(unitClass?.unitPrice ?? 1)}
                    </Typography>
                  </Box>
                </Stack>
                <Stack
                  useFlexGap
                  direction="row"
                  spacing={3}
                  sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Target return
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {unitClass?.targetReturnPct}% p.a.
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Income FY2027
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {money(income)}
                    </Typography>
                  </Box>
                  {holding.commitment !== undefined && (
                    <Box sx={{ minWidth: 200, flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Called {money(holding.costBase)} of {money(holding.commitment)}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(holding.costBase / holding.commitment) * 100}
                        sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                      />
                    </Box>
                  )}
                </Stack>
              </Card>
            );
          })}
        </Stack>
        <Card sx={{ alignSelf: 'start' }}>
          <Typography variant="h6">Allocation by fund</Typography>
          <Box sx={{ height: 220 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  isAnimationActive={false}
                  data={byFund}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={2}
                >
                  {byFund.map((entry, index) => (
                    <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => money(value)} />
              </PieChart>
            </ResponsiveContainer>
          </Box>
          {byFund.map((row, index) => (
            <Stack key={row.name} direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: COLORS[index % COLORS.length],
                  }}
                />
                <Typography variant="body2">{row.name}</Typography>
              </Stack>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {total ? `${Math.round((row.value / total) * 100)}%` : '—'}
              </Typography>
            </Stack>
          ))}
        </Card>
      </Box>
      <CompletedInvestments
        entity={entity}
        onOpenExit={(holderId, fundId) =>
          setViewing(
            scope.documents.find(
              (doc) =>
                doc.kind === 'EXIT_STATEMENT' && doc.holderId === holderId && doc.fundId === fundId,
            ) ?? null,
          )
        }
      />
      <DocumentViewerDialog doc={viewing} onClose={() => setViewing(null)} />
    </>
  );
}
